import React, { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';

interface ForgotPasswordFormProps {
  onToggleMode: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onToggleMode }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { resetPassword } = useAuth();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error } = await resetPassword(email);
    setLoading(false);

    if (error) {
      setError(error.message || 'Failed to send reset email.');
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <>
        {/* Logo */}
        <div className="mb-10">
          <img src="/logo_horizontal.png" alt="NoteWell" className="h-8 w-auto" />
        </div>

        <h1 className="text-3xl font-semibold">Check your email</h1>
        <p className="mt-2 text-sm text-zinc-500">We've sent a password reset link to {email}</p>

        <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-700">
            If an account exists that matches your email address, you will receive an email with
            instructions to reset your password.
          </p>
        </div>

        {/* Back to sign in */}
        <p className="mt-6 text-center text-sm text-zinc-500">
          <button type="button" onClick={onToggleMode} className="text-zinc-900 hover:underline">
            Back to Sign In
          </button>
        </p>
      </>
    );
  }

  return (
    <>
      {/* Logo */}
      <div className="mb-10">
        <img src="/logo_horizontal.png" alt="NoteWell" className="h-8 w-auto" />
      </div>

      <h1 className="text-3xl font-semibold">Forgot your password?</h1>
      <p className="mt-2 text-sm text-zinc-500">
        Type in your email and we'll send you a link to reset the password
      </p>

      {/* Error Banner */}
      {error && (
        <div className="mt-6 mb-8 p-4 bg-red-100 border border-red-200 rounded-lg">
          <div className="text-sm text-red-900">
            {error.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        </div>
      )}

      {/* Email form */}
      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm mb-1">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder-zinc-400 focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="you@example.com"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full rounded-md bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending reset code…' : 'Send reset code'}
        </button>
      </form>

      {/* Back to sign in */}
      <p className="mt-6 text-center text-sm text-zinc-500">
        Already have an account?{' '}
        <button type="button" onClick={onToggleMode} className="text-zinc-900 hover:underline">
          Sign In
        </button>
      </p>
    </>
  );
};

export default ForgotPasswordForm;
